/**
 * Study Assignment Utilities
 * Helper functions for study assignment operations
 */

import { API_CONFIG, API_ENDPOINTS } from '../config/api.config';
import type { SelectedStudy } from './studyUtils';

import authService from '../auth/authService';

/** Map study API source_facility_id to assign payload (empty string → null). */
export function normalizeSourceFacilityIdForApi(raw?: string | number | null): number | null {
  if (raw === null || raw === undefined) {
    return null;
  }
  const trimmed = String(raw).trim();
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
}

export interface AssignmentPayload {
  study_instance_uid: string;
  modality_id: number;
  source_facility_id?: number | null;
}

export interface ReassignmentPayload {
  study_instance_uid: string;
  radiologist_id: number;
  status: string;
  assignment_status: string;
  modality_id: number;
}

export interface BatchAssignmentPayload {
  radiologist_id: number;
  assignment_status: string;
  studies: AssignmentPayload[];
}

export interface BatchReassignmentPayload {
  radiologist_id: number;
  assignment_status: string;
  studies: Array<{
    study_id: number;
    modality_id: number;
  }>;
}

export interface BatchHubAssignmentPayload {
  hub_facility_id: number;
  studies: Array<{ study_instance_uid: string }>;
}

export interface HubRoutingUnassignPayload {
  action: 'unassign-hub';
  studies: Array<{ study_instance_uid: string }>;
}

export interface HubRoutingReassignPayload {
  action: 'reassign-hub';
  hub_facility_id: number;
  studies: Array<{ study_instance_uid: string }>;
}

export type HubRoutingPayload = HubRoutingUnassignPayload | HubRoutingReassignPayload;

export interface HubRoutingResultItem {
  study_id?: number;
  study_instance_uid?: string;
  action?: string;
  facility_id?: number;
  assigned_hub_facility_id?: number | null;
  source_facility_id?: number | null;
  assignment_status?: string | null;
  radiologist_id?: number | null;
  status?: string;
  error?: string;
  [key: string]: unknown;
}

export interface HubRoutingBatchResult {
  success: HubRoutingResultItem[];
  failed: HubRoutingResultItem[];
  summary?: {
    total?: number;
    succeeded?: number;
    failed?: number;
  };
}

/**
 * Get authorization headers for API requests
 */
export function getAuthHeaders(): HeadersInit {
  const token = authService.getAccessToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    accept: 'application/json, text/plain, */*',
    'tenant-id': API_CONFIG.TENANT_ID,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

export interface AssignmentResult {
  id?: number;
  study_instance_uid?: string;
  modality_id?: number;
  status?: string;
  radiologist_id?: number;
  assignment_status?: string;
  [key: string]: unknown;
}

/**
 * Make batch assignment API call
 */
export async function batchAssignStudies(
  payload: BatchAssignmentPayload
): Promise<AssignmentResult[]> {
  const response = await fetch(API_ENDPOINTS.STUDIES.ASSIGN, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to assign studies: ${response.statusText}`);
  }

  const responseData = await response.json();
  if (!responseData.success) {
    throw new Error(responseData.message || 'Failed to assign studies');
  }

  // Handle batch response - it might return an array or a single object
  if (Array.isArray(responseData.data)) {
    return responseData.data;
  } else if (responseData.data) {
    return [responseData.data];
  }

  return [];
}

/**
 * Make batch reassignment API call
 */
export async function batchReassignStudies(
  payload: BatchReassignmentPayload
): Promise<AssignmentResult[]> {
  const response = await fetch(API_ENDPOINTS.STUDIES.REASSIGN, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to reassign studies: ${response.statusText}`);
  }

  const responseData = await response.json();
  if (!responseData.success) {
    throw new Error(responseData.message || 'Failed to reassign studies');
  }

  // Handle batch response - extract successful reassignments
  const { data } = responseData;
  if (data?.success && Array.isArray(data.success)) {
    return data.success;
  } else if (data) {
    return [data];
  }

  return [];
}

/**
 * Convert reassignments to batch format with study_id
 */
function parseHubRoutingBatchResult(data: Record<string, unknown>): HubRoutingBatchResult {
  return {
    success: Array.isArray(data.success) ? (data.success as HubRoutingResultItem[]) : [],
    failed: Array.isArray(data.failed) ? (data.failed as HubRoutingResultItem[]) : [],
    summary:
      data.summary && typeof data.summary === 'object'
        ? (data.summary as HubRoutingBatchResult['summary'])
        : undefined,
  };
}

export async function batchHubRoutingStudies(
  payload: HubRoutingPayload
): Promise<HubRoutingBatchResult> {
  const response = await fetch(API_ENDPOINTS.STUDIES.HUB_ROUTING, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to update hub routing: ${response.statusText}`);
  }

  const responseData = await response.json();
  if (!responseData.success) {
    throw new Error(responseData.message || 'Failed to update hub routing');
  }

  return parseHubRoutingBatchResult(responseData.data || {});
}

export async function batchAssignStudiesToHub(payload: BatchHubAssignmentPayload): Promise<{
  success: AssignmentResult[];
  failed: Array<{ study_instance_uid?: string; error?: string }>;
}> {
  const response = await fetch(API_ENDPOINTS.STUDIES.ASSIGN_HUB, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to assign studies to hub: ${response.statusText}`);
  }

  const responseData = await response.json();
  if (!responseData.success) {
    throw new Error(responseData.message || 'Failed to assign studies to hub');
  }

  const data = responseData.data || {};
  return {
    success: Array.isArray(data.success) ? data.success : [],
    failed: Array.isArray(data.failed) ? data.failed : [],
  };
}

export function convertReassignmentsToBatchFormat(
  reassignments: ReassignmentPayload[],
  selectedStudies: SelectedStudy[]
): Array<{ study_id: number; modality_id: number }> {
  return reassignments.map(reassignment => {
    const study = selectedStudies.find(s => s.studyInstanceUid === reassignment.study_instance_uid);
    if (!study || !study.studyId) {
      throw new Error(
        `Study ${reassignment.study_instance_uid} does not have a study_id and cannot be reassigned`
      );
    }
    return {
      study_id: Number(study.studyId),
      modality_id: reassignment.modality_id,
    };
  });
}
